import type {MouseEventHandler} from 'react'
import type {LucideIcon} from 'lucide-react'
import {cn} from '@/lib/utils'

interface IconProps {
    icon: LucideIcon
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    muted?: boolean
    /** Rotate the icon 90° clockwise. Used for expand/collapse chevrons. Transitions smoothly. */
    rotated?: boolean
    /** Apply `animate-spin`. Use for refresh/sync icons while a mutation is pending. */
    spin?: boolean
    className?: string
    /** When set, the icon becomes clickable (cursor-pointer + onClick). Use sparingly — prefer Button for semantic actions. */
    onClick?: MouseEventHandler<SVGSVGElement>
    'data-testid'?: string
    'aria-label'?: string
}

const SIZE_CLASS: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string> = {
    xs: 'h-3 w-3',
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
    xl: 'h-8 w-8',
}

export function Icon({
    icon: IconComponent,
    size = 'md',
    muted,
    rotated,
    spin,
    className,
    onClick,
    'data-testid': testId,
    'aria-label': ariaLabel,
}: IconProps) {
    return (
        <IconComponent
            className={cn(
                SIZE_CLASS[size],
                'shrink-0',
                muted && 'text-muted-foreground',
                rotated !== undefined && 'transition-transform',
                rotated && 'rotate-90',
                spin && 'animate-spin',
                onClick && 'cursor-pointer',
                className,
            )}
            onClick={onClick}
            data-testid={testId}
            aria-label={ariaLabel}
        />
    )
}
